import { supabase } from "@/integrations/supabase/client";

// Manual 2.0 lives in Help-Genie-Consumer (prototype/) and is served from the
// main site. A product QR opens the product's manual page there.
export const MANUAL_HUB_ORIGIN = "https://helpgenie.ai";
export const MANUAL_PRODUCT_PATH = "/hub/prototype/product.html";

/** A catalogue product a genie can answer for, as the QR picker shows it. */
export interface GenieProduct {
  id: string;
  name: string;
  slug: string;
  model_number: string | null;
  brand: string | null;
}

/** `https://helpgenie.ai/hub/prototype/product.html?product=<slug>&src=qr` */
export function productManualUrl(slug: string, source = "qr"): string {
  return `${MANUAL_HUB_ORIGIN}${MANUAL_PRODUCT_PATH}?product=${encodeURIComponent(slug)}&src=${encodeURIComponent(source)}`;
}

interface ProductRow {
  id: string;
  name: string;
  slug: string;
  model_number: string | null;
  marketplace_brands: { name: string } | { name: string }[] | null;
}

/** Dedupes by id and sorts by name so a genie listed twice never repeats a product. */
export function normaliseGenieProducts(rows: ProductRow[]): GenieProduct[] {
  const byId = new Map<string, GenieProduct>();
  for (const row of rows) {
    if (!row?.id || !row.slug || byId.has(row.id)) continue;
    const brand = Array.isArray(row.marketplace_brands) ? row.marketplace_brands[0] : row.marketplace_brands;
    byId.set(row.id, {
      id: row.id,
      name: row.name,
      slug: row.slug,
      model_number: row.model_number ?? null,
      brand: brand?.name ?? null,
    });
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

const MAX_PRODUCTS = 300;

/**
 * Where a "product" exists in Voice: `marketplace_genies` has no product_id.
 * A marketplace genie serves every `marketplace_products` row in its category
 * (narrowed to its brand when it has one), exactly how the consumer manual page
 * picks the genie for a product (Help-Genie-Consumer prototype/GenieService.js).
 */
export class ProductGenieService {
  static async listProductsForGenie(agentId: string): Promise<GenieProduct[]> {
    const { data: listings, error } = await supabase
      .from("marketplace_genies")
      .select("brand_id, category_id")
      .eq("agent_id", agentId)
      .limit(5);
    if (error) throw error;
    if (!listings || listings.length === 0) return [];

    const rows: ProductRow[] = [];
    for (const listing of listings) {
      if (!listing.category_id) continue;
      let query = supabase
        .from("marketplace_products")
        .select("id, name, slug, model_number, marketplace_brands(name)")
        .eq("category_id", listing.category_id)
        .order("name", { ascending: true })
        .limit(MAX_PRODUCTS);
      if (listing.brand_id) query = query.eq("brand_id", listing.brand_id);
      const { data, error: productsError } = await query;
      if (productsError) throw productsError;
      rows.push(...((data ?? []) as unknown as ProductRow[]));
    }
    return normaliseGenieProducts(rows);
  }
}
